// src/pages/Admin-Dashboard/admin-dashboard.jsx
import React, { useEffect, useState } from "react";
import {
  Users,
  Layers,
  Activity,
  RefreshCw,
  Plus,
  Shield,
  TrendingUp,
  Layout,
  Clock,
  CheckCircle,
  X,
  PlusCircle
} from "lucide-react";
import {
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

import AddStaffModal from "../../components/AddStaffModal";
import Sidebar from "../../components/admin-sidebar";
import Topbar from "../../components/TopbarNav";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { fetchCurrentUser } from "../../api/user-api"; 
import { fetchLogs } from "../../api/admin-api"; 
import { fetchDashboardData } from "../../api/dashboard-api"; 
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [userStatusData, setUserStatusData] = useState([]);

  // Granular loading states
  const [statsLoading, setStatsLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    async function fetchAllData() {
      setStatsLoading(true);
      setChartsLoading(true);
      fetchDashboardData()
        .then(dashboardData => {
          setStats({
            totalUsers: dashboardData.totalUsers,
            hrUsers: dashboardData.hrUsers,
            projectLeads: dashboardData.projectLeads,
            fieldStaff: dashboardData.fieldStaff,
            others: dashboardData.others,
            systemHealth: dashboardData.systemHealth,
            apiUptime: dashboardData.apiUptime,
            recentErrors: dashboardData.recentErrors,
            pendingApprovals: dashboardData.pendingApprovals,
          });
          setUserGrowthData(dashboardData.userGrowth);
          setUserStatusData(dashboardData.userStatus);
        })
        .catch(error => {
          console.error("Failed to fetch dashboard data:", error);
          toast.error("Failed to fetch dashboard data");
        })
        .finally(() => {
          setStatsLoading(false);
          setChartsLoading(false);
        });

      setLogsLoading(true);
      fetchLogs()
        .then(logs => setActivityLogs(logs))
        .catch(error => {
          console.error("Failed to fetch logs:", error);
          toast.error("Failed to fetch logs");
        })
        .finally(() => setLogsLoading(false));
    }
    
    const token = localStorage.getItem("token");
    if (token) {
      fetchCurrentUser(token)
        .catch(error => {
          console.error("Failed to fetch current user:", error);
        });
    }

    fetchAllData();
  }, []);

  const getHealthColor = (status) => {
    switch (status) {
      case "Operational": return "text-emerald-600";
      case "Minor Issues": return "text-amber-500";
      case "Major Outage": return "text-red-600";
      default: return "text-gray-400";
    }
  };

  const userStatusColors = ["#f97316", "#fbbf24", "#10b981", "#ef4444"];

  const refreshData = () => {
    setStatsLoading(true);
    setChartsLoading(true);
    setLogsLoading(true);
    
    fetchDashboardData()
      .then(dashboardData => {
        setStats({
          totalUsers: dashboardData.totalUsers,
          hrUsers: dashboardData.hrUsers,
          projectLeads: dashboardData.projectLeads,
          fieldStaff: dashboardData.fieldStaff,
          others: dashboardData.others,
          systemHealth: dashboardData.systemHealth,
          apiUptime: dashboardData.apiUptime,
          recentErrors: dashboardData.recentErrors,
          pendingApprovals: dashboardData.pendingApprovals,
        });
        setUserGrowthData(dashboardData.userGrowth);
        setUserStatusData(dashboardData.userStatus);
      })
      .catch(() => toast.error("Failed to refresh dashboard"))
      .finally(() => {
        setStatsLoading(false);
        setChartsLoading(false);
      });

    fetchLogs()
      .then(logs => setActivityLogs(logs))
      .catch(() => toast.error("Failed to refresh logs"))
      .finally(() => setLogsLoading(false));
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <Sidebar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="System" 
                lastWordAccent="Overview" 
                subtitle="Administrative control center for monitoring system health, user growth, and audit logs." 
              />
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={refreshData}
                  disabled={statsLoading || logsLoading || chartsLoading}
                  className="flex items-center gap-3 bg-white text-gray-900 border border-gray-100 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-gray-50 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                >
                  <RefreshCw size={16} strokeWidth={3} className={statsLoading || logsLoading || chartsLoading ? 'animate-spin' : ''} /> Synchronize
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddUserModal(true)}
                  className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95 whitespace-nowrap"
                >
                  <PlusCircle size={16} strokeWidth={3} /> Provision Account
                </motion.button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {statsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-40 bg-white rounded-[2rem] animate-pulse border border-gray-100 shadow-sm" />
                ))
              ) : (
                <>
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <PremiumStatCard 
                      title="Total Users" 
                      value={stats?.totalUsers || "0"} 
                      icon={<Users className="text-orange-600" />} 
                      iconBg="bg-orange-50"
                      description={`HR: ${stats?.hrUsers || 0} • Leads: ${stats?.projectLeads || 0}`}
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <PremiumStatCard 
                      title="System Status" 
                      value={stats?.systemHealth || "N/A"} 
                      icon={<Activity className={getHealthColor(stats?.systemHealth)} />} 
                      iconBg="bg-white"
                      description={`API: ${stats?.apiUptime || 'N/A'} • Errors: ${stats?.recentErrors || 0}`}
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <PremiumStatCard 
                      title="Activity Metrics" 
                      value={activityLogs.length || "0"} 
                      icon={<Layers className="text-blue-600" />} 
                      iconBg="bg-blue-50"
                      description="Last 24 hours audit volume"
                    />
                  </motion.div>
                </>
              )}
            </div>

            {/* Recent Activity Section */}
            <div className="mb-12">
              <PremiumCard title="System Audit Narrative" icon={<Clock size={18} />}>
                <div className="pt-2">
                  {logsLoading ? (
                    <div className="flex justify-center items-center py-20">
                      <div className="animate-spin rounded-xl h-10 w-10 border-t-2 border-orange-500"></div>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                      {activityLogs.length > 0 ? (
                        activityLogs.map((log) => (
                          <div key={log._id || log.id} className="group flex items-center justify-between p-5 hover:bg-gray-50/50 transition-all rounded-2xl">
                            <div className="flex-1">
                              <div className="font-black text-xs text-gray-800 uppercase tracking-widest">{log.name} {log.action || log.event}</div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mt-1.5 flex items-center gap-2">
                                <span className={`size-1.5 rounded-full ${log.severity === 'error' ? 'bg-red-500' : 'bg-orange-400'}`} />
                                {log.module || 'CORE ENGINE'}
                              </div>
                            </div>
                            <span className="text-[10px] font-black text-gray-300 uppercase tracking-[0.15em] group-hover:text-orange-500 transition-colors bg-gray-50/50 px-3 py-1 rounded-lg">
                              {Math.floor((Date.now() - new Date(log.timestamp)) / 60000)}m ago
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-24">
                          <CheckCircle size={48} strokeWidth={1} className="mx-auto text-orange-100 mb-6" />
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No active audit logs found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </PremiumCard>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 pb-12">
              {chartsLoading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-96 bg-white border border-gray-100 rounded-[2.5rem] animate-pulse shadow-sm" />
                ))
              ) : (
                <>
                  <PremiumCard title="Strategic Growth Analytics" icon={<TrendingUp size={18} />}>
                    <div className="h-[300px] py-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={userGrowthData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }}
                          />
                          <Line type="monotone" dataKey="users" stroke="#f97316" strokeWidth={4} dot={{ r: 5, fill: '#fff', strokeWidth: 3, stroke: '#f97316' }} activeDot={{ r: 7, strokeWidth: 0, fill: '#f97316' }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>

                  <PremiumCard title="Platform Status Mix" icon={<Layout size={18} />}>
                    <div className="h-[300px] py-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={userStatusData}
                            cx="50%"
                            cy="45%"
                            outerRadius={95}
                            innerRadius={65}
                            paddingAngle={8}
                            dataKey="value"
                            stroke="none"
                          >
                            {userStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={userStatusColors[index % userStatusColors.length]} cornerRadius={12} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                          <Legend 
                            verticalAlign="bottom" 
                            iconType="circle"
                            formatter={(value) => <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{value}</span>}
                            wrapperStyle={{ paddingTop: '20px' }} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      <AnimatePresence>
        {showAddUserModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setShowAddUserModal(false)} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-100"
            >
              <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                    <PlusCircle size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest">Add New User</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">Create a new system principal account</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAddUserModal(false)} 
                  className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="p-10 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar">
                <AddStaffModal
                  closeForm={() => setShowAddUserModal(false)}
                  onUserAdded={() => {
                    setShowAddUserModal(false);
                    refreshData();
                  }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}